import { DerbyRngV1 } from '../rng';
import { DRILL_TICK_S, MASK_HELD, type MinigameDef, type MinigameSession, type SessionScore } from './framework';

export type TrustPhase = 'approach' | 'hesitate' | 'calm' | 'gap' | 'over';

/** Composure sequence: approach, listen when she hesitates, then recommit. */
export class TrustSession implements MinigameSession {
  readonly obstacles = 4;
  phase: TrustPhase = 'approach';
  phaseTick = 0;
  obstacle = 0;
  calm = 1;
  clean = 0;
  private approachTicks: number;
  private listened = false;
  private committed = false;
  private readonly rng: DerbyRngV1;
  private readonly drainMul: number;

  constructor(readonly tier: number, seed: number, mods?: Record<string, number>) {
    this.rng = new DerbyRngV1(seed).fork('trust');
    this.approachTicks = this.drawApproach();
    this.drainMul = mods?.penDrainMul ?? 1;
  }

  get finished(): boolean { return this.phase === 'over' || this.calm <= 0; }
  phaseDurationTicks(): number {
    if (this.phase === 'approach') return this.approachTicks;
    if (this.phase === 'hesitate') return Math.max(7, 14 - this.tier);
    if (this.phase === 'calm') return 12;
    if (this.phase === 'gap') return 10;
    return 1;
  }
  private drawApproach(): number { return Math.round(this.rng.range(0.8, 1.4) / DRILL_TICK_S); }
  private fail(amount: number): void { this.calm = Math.max(0, this.calm - amount * this.drainMul); }
  private nextObstacle(): void {
    if (this.listened && this.committed) this.clean += 1;
    this.obstacle += 1;
    if (this.obstacle >= this.obstacles) { this.phase = 'over'; return; }
    this.phase = 'gap'; this.phaseTick = 0; this.listened = false; this.committed = false;
  }

  step(mask: number): void {
    if (this.finished) return;
    const held = (mask & MASK_HELD) !== 0;
    if (this.phase === 'approach') {
      if (!held) this.fail(0.0025);
      this.phaseTick += 1;
      if (this.phaseTick >= this.approachTicks) { this.phase = 'hesitate'; this.phaseTick = 0; }
      return;
    }
    if (this.phase === 'hesitate') {
      if (!held) this.listened = true;
      else this.fail(0.012);
      this.phaseTick += 1;
      const listenTicks = Math.max(7, 14 - this.tier);
      if (this.phaseTick >= listenTicks) { this.phase = 'calm'; this.phaseTick = 0; }
      return;
    }
    if (this.phase === 'calm') {
      if (held) this.committed = true;
      this.phaseTick += 1;
      if (this.phaseTick >= 12) { if (!this.committed) this.fail(0.1); this.nextObstacle(); }
      return;
    }
    if (this.phase === 'gap') {
      this.phaseTick += 1;
      if (this.phaseTick >= 10) { this.phase = 'approach'; this.phaseTick = 0; this.approachTicks = this.drawApproach(); }
    }
  }

  score(): SessionScore {
    const raw = Math.max(0, Math.min(1, 0.8 * (this.clean / this.obstacles) + 0.2 * this.calm));
    return { raw, detail: `${this.clean}/${this.obstacles} trusted obstacles · calm ${Math.round(this.calm * 100)}%`, failed: this.calm <= 0 };
  }
}

export const TRUST_DEF: MinigameDef = {
  id: 'trust', name: 'Trust Course', stat: 'heart', energyCost: 4,
  create: (tier, seed, mods) => new TrustSession(tier, seed, mods),
};
